import React, { useState } from "react";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import { apiService } from "../../api/apiConnect";
import { ENDPOINTS } from "../../api/endpoint";
import { useNavigate } from "react-router-dom";
import "./support_request.css";

const SupportRequest = () => {
  const navigate = useNavigate();
  const [fileNames, setFileNames] = useState({
    dmsFile1: "",
    dmsFile2: "",
    dmsFile3: "",
    dmsFile4: "",
    supportFile1: "",
    supportFile2: "",
    supportFile3: "",
    supportFile4: "",
  });
  const [apiErrors, setApiErrors] = useState({});

  // Correction type tips mapping
  const correctionTips = {
    AC: (
      <div>
        <p>
          Documentation from the reporting source, police department, or state
          agency indicating the accident record was incorrect or reported in
          error.
        </p>
        <p>
          Please Note - Customer Support may be able to contact the accident
          source if a contact on file currently exists.
        </p>
      </div>
    ),
    AA: (
      <div>
        <p>
          Please reach out to the reporting auction to confirm auction
          announcement details. The reporting auction will contact Experian to
          complete any necessary corrections.
        </p>
      </div>
    ),
    ST: (
      <div>
        <p>
          Letter or email from state DMV in which title brand was reported
          stating that the record was issued in error and has been corrected.
        </p>
        <p>
          Clear title from reporting state or a title that indicates "Corrected
          or Replacement" title status.
        </p>
        <p>Clear title from reporting state, either current or corrected.</p>
      </div>
    ),
    DS: (
      <div>
        <p>
          For collision related damage records, a police report or other state
          agency document detailing the extent of the damage is acceptable.
        </p>
        <p>
          For non-collision related damage, a repair order or third party
          inspection outlining the extent of the damage is acceptable.
        </p>
      </div>
    ),
    OR: (
      <div>
        <p>
          <strong>State DMV Records:</strong>
          <ul>
            <li>
              Letter or email from state DMV stating record was issued in error
              and has been corrected.
            </li>
            <li>Clear or corrected title from reporting state</li>
          </ul>
        </p>
        <p>
          <strong>Auction Records:</strong>
          <ul>
            <li>
              Please reach out to the reporting auction to confirm auction
              announcement details. The reporting auction will contact Experian
              to complete any necessary corrections.
            </li>
          </ul>
        </p>
        <p>
          <strong>
            Independent Source, Inspection, or Dealer Service Records:
          </strong>
          <ul>
            <li>
              Odometer disclosure statement signed by current owner verifying
              current mileage is accurate.
            </li>
            <li>
              Appraisals or state title records dated after the odometer reading
              in question.
            </li>
            <li>
              Service records before and after incorrect odometer reading.
            </li>
          </ul>
        </p>
      </div>
    ),
  };

  // Validation schema
  const getValidationSchema = () => {
    return Yup.object().shape({
      firstName: Yup.string()
        .max(20, "Max 20 characters")
        .required("First Name is required"),
      lastName: Yup.string()
        .max(35, "Max 35 characters")
        .required("Last Name is required"),
      email: Yup.string()
        .required("Email address is required.")
        .test(
          "email-format",
          "Email address is an invalid e-mail address.",
          (value) => {
            if (!value) return true; // Required check is handled separately
            const trimmed = value.trim();
            const atpos = trimmed.indexOf("@");
            const dotpos = trimmed.lastIndexOf(".");
            return (
              atpos >= 1 && dotpos > atpos + 1 && dotpos + 2 <= trimmed.length
            );
          }
        ),
      confirmEmail: Yup.string()
        .required("Confirm Email address is required.")
        .oneOf([Yup.ref("email"), null], "Email address does not match.")
        .test(
          "email-format",
          "Confirm Email address is an invalid e-mail address.",
          (value) => {
            if (!value) return true; // Required check is handled separately
            const trimmed = value.trim();
            const atpos = trimmed.indexOf("@");
            const dotpos = trimmed.lastIndexOf(".");
            return (
              atpos >= 1 && dotpos > atpos + 1 && dotpos + 2 <= trimmed.length
            );
          }
        ),
      autocheckId: Yup.string()
        .max(7, "Max 7 characters")
        .required("AutoCheck ID is required"),
      coxId: Yup.string()
        .max(10, "Max 10 characters")
        .required("Cox ID is required"),
      supportType: Yup.string().required("Support Type is required"),
      details: Yup.string().max(600, "Max 600 characters"),
      // Confirmation is handled with alert in handleSubmit to match legacy UI
      confirmation: Yup.boolean(),
      // DCR fields
      vin: Yup.string().when("supportType", {
        is: "DCR",
        then: (schema) =>
          schema.max(17, "Max 17 characters").required("VIN is required"),
        otherwise: (schema) => schema.notRequired(),
      }),
      disputedDate: Yup.string().when("supportType", {
        is: "DCR",
        then: (schema) => schema.required("Disputed Date is required"),
        otherwise: (schema) => schema.notRequired(),
      }),
      correctionType: Yup.string().when("supportType", {
        is: "DCR",
        then: (schema) => schema.required("Correction Type is required"),
        otherwise: (schema) => schema.notRequired(),
      }),
      // SDS fields
      dmsType: Yup.string().when("supportType", {
        is: "SDS",
        then: (schema) => schema.required("DMS Type is required"),
        otherwise: (schema) => schema.notRequired(),
      }),
      dealershipName: Yup.string().when("supportType", {
        is: "SDS",
        then: (schema) =>
          schema
            .max(50, "Max 50 characters")
            .required("Dealership Name is required"),
        otherwise: (schema) => schema.notRequired(),
      }),
      dealershipAddress: Yup.string().when("supportType", {
        is: "SDS",
        then: (schema) =>
          schema
            .max(150, "Max 150 characters")
            .required("Dealership Address is required"),
        otherwise: (schema) => schema.notRequired(),
      }),
      dealershipCity: Yup.string().when("supportType", {
        is: "SDS",
        then: (schema) =>
          schema
            .max(100, "Max 100 characters")
            .required("Dealership City is required"),
        otherwise: (schema) => schema.notRequired(),
      }),
      dealershipState: Yup.string().when("supportType", {
        is: "SDS",
        then: (schema) => schema.required("Dealership State is required"),
        otherwise: (schema) => schema.notRequired(),
      }),
      dealershipZip: Yup.string().when("supportType", {
        is: "SDS",
        then: (schema) =>
          schema
            .max(10, "Max 10 characters")
            .required("Dealership ZipCode is required"),
        otherwise: (schema) => schema.notRequired(),
      }),
      dealershipPhone: Yup.string().when("supportType", {
        is: "SDS",
        then: (schema) =>
          schema
            .required("Dealership Phone is required")
            .matches(
              /^(\d{3}-\d{3}-\d{4}|\(\d{3}\)\d{3}-\d{4})$/,
              "Enter a valid phone number 123-456-7890 or (123)456-7890."
            )
            .max(14, "Max 14 characters"),
        otherwise: (schema) => schema.notRequired(),
      }),
      dmsDealerNumber: Yup.string().when("supportType", {
        is: "SDS",
        then: (schema) =>
          schema
            .max(35, "Max 35 characters")
            .required("DMS-Specific Dealer Number is required"),
        otherwise: (schema) => schema.notRequired(),
      }),
      techContactName: Yup.string().when("supportType", {
        is: "SDS",
        then: (schema) =>
          schema
            .max(50, "Max 50 characters")
            .required("Tech Contact Name is required"),
        otherwise: (schema) => schema.notRequired(),
      }),
      techContactPhone: Yup.string().when("supportType", {
        is: "SDS",
        then: (schema) =>
          schema
            .required("Tech Contact Phone is required")
            .matches(
              /^(\d{3}-\d{3}-\d{4}|\(\d{3}\)\d{3}-\d{4})$/,
              "Enter a valid phone number 123-456-7890 or (123)456-7890."
            )
            .max(14, "Max 14 characters"),
        otherwise: (schema) => schema.notRequired(),
      }),
      techContactEmail: Yup.string().when("supportType", {
        is: "SDS",
        then: (schema) =>
          schema
            .required("Tech Contact Email is required.")
            .max(50, "Max 50 characters")
            .test(
              "email-format",
              "Tech Contact Email address is an invalid e-mail address.",
              (value) => {
                if (!value) return true; // Required check is handled separately
                const trimmed = value.trim();
                const atpos = trimmed.indexOf("@");
                const dotpos = trimmed.lastIndexOf(".");
                return (
                  atpos >= 1 &&
                  dotpos > atpos + 1 &&
                  dotpos + 2 <= trimmed.length
                );
              }
            ),
        otherwise: (schema) => schema.notRequired(),
      }),
      techContactConfirmEmail: Yup.string().when("supportType", {
        is: "SDS",
        then: (schema) =>
          schema
            .required("Confirm Tech Contact Email is required.")
            .oneOf(
              [Yup.ref("techContactEmail"), null],
              "Email address does not match."
            )
            .test(
              "email-format",
              "Confirm Tech Contact Email address is an invalid e-mail address.",
              (value) => {
                if (!value) return true; // Required check is handled separately
                const trimmed = value.trim();
                const atpos = trimmed.indexOf("@");
                const dotpos = trimmed.lastIndexOf(".");
                return (
                  atpos >= 1 &&
                  dotpos > atpos + 1 &&
                  dotpos + 2 <= trimmed.length
                );
              }
            ),
        otherwise: (schema) => schema.notRequired(),
      }),
    });
  };

  const initialValues = {
    firstName: "",
    lastName: "",
    email: "",
    confirmEmail: "",
    autocheckId: "",
    coxId: "",
    supportType: "",
    details: "",
    confirmation: false,
    // DCR fields
    vin: "",
    disputedDate: "",
    correctionType: "",
    // SDS fields
    dmsType: "",
    dealershipName: "",
    dealershipAddress: "",
    dealershipCity: "",
    dealershipState: "",
    dealershipZip: "",
    dealershipPhone: "",
    dmsDealerNumber: "",
    techContactName: "",
    techContactPhone: "",
    techContactEmail: "",
    techContactConfirmEmail: "",
  };

  const handleFileChange = (fieldName, file, setFieldValue) => {
    if (file) {
      setFieldValue(fieldName, file);
      setFileNames((prev) => ({
        ...prev,
        [fieldName]: file.name,
      }));
    } else {
      setFieldValue(fieldName, null);
      setFileNames((prev) => ({
        ...prev,
        [fieldName]: "",
      }));
    }
  };

  const handleSubmit = async (values, { setFieldError, setSubmitting }) => {
    // Check if at least one file attachment is required for specific DMS Types (check before terms and conditions)
    const dmsTypesRequiringFiles = ["AUT", "ATM", "DBT", "DOM", "PBS", "TEK"]; // AutoSoft, Automate, Dealer Built, Dominion Access (ACS), PBS, Tekion
    if (
      values.supportType === "SDS" &&
      dmsTypesRequiringFiles.includes(values.dmsType)
    ) {
      const hasDmsFile =
        values.dmsFile1 ||
        values.dmsFile2 ||
        values.dmsFile3 ||
        values.dmsFile4;
      if (!hasDmsFile) {
        alert(
          "At least one file attachment is required for the selected DMS Type selected."
        );
        setSubmitting(false);
        return;
      }
    }

    // Check terms and conditions after all other validations pass
    if (!values.confirmation) {
      alert("Please agree to terms and conditions to continue.");
      setSubmitting(false);
      return;
    }

    // Clear previous errors
    setApiErrors({});

    const formData = new FormData();

    // Append all form values (excluding files)
    Object.keys(values).forEach((key) => {
      if (
        values[key] !== "" &&
        values[key] !== null &&
        values[key] !== undefined &&
        values[key] !== false &&
        !(values[key] instanceof File)
      ) {
        formData.append(key, values[key]);
      }
    });

    // Append files if they exist
    Object.keys(values).forEach((key) => {
      if (values[key] instanceof File) {
        formData.append(key, values[key]);
      }
    });

    await apiService(
      "POST",
      `${ENDPOINTS.SUPPORT_REQUEST}`,
      formData,
      {},
      (response) => {
        navigate("/thank-you");
      },
      (error) => {
        console.error("API Error:", error);
        setSubmitting(false);

        const errors = {};

        // Handle array of validation errors (format: [{ field: "dmsFile1", message: "..." }])
        // This is what getErrorMessage returns for 400 errors: error.response?.data?.data
        if (Array.isArray(error)) {
          error.forEach((err) => {
            if (err && typeof err === "object" && err.field && err.message) {
              // Set Formik field error
              setFieldError(err.field, err.message);
              // Also store in apiErrors for general display
              errors[err.field] = err.message;
            } else if (typeof err === "string") {
              // If it's just a string, add as general error
              errors.general = err;
            }
          });
        }
        // Handle error object with data property (format: { message: "Validation Error", data: [...] })
        else if (error && typeof error === "object") {
          // Handle validation errors array in data property
          if (error.data && Array.isArray(error.data)) {
            error.data.forEach((err) => {
              if (err.field && err.message) {
                setFieldError(err.field, err.message);
                errors[err.field] = err.message;
              }
            });
          }

          // Handle error.errors object (alternative format)
          if (error.errors && typeof error.errors === "object") {
            Object.keys(error.errors).forEach((key) => {
              const errorMsg = Array.isArray(error.errors[key])
                ? error.errors[key].join(", ")
                : error.errors[key];
              setFieldError(key, errorMsg);
              errors[key] = errorMsg;
            });
          }

          // Handle general error message if no field-specific errors
          if (error.message && Object.keys(errors).length === 0) {
            errors.general = error.message;
          }
        }
        // Handle string error
        else if (typeof error === "string") {
          errors.general = error;
        }

        setApiErrors(errors);
      }
    );
  };

  // DMS Types with display name and value
  const dmsTypes = [
    { displayName: "AutoSoft", value: "AUT" },
    { displayName: "Automate", value: "ATM" },
    { displayName: "ADAM System", value: "ADA" },
    { displayName: "CDK", value: "CDK" },
    { displayName: "Dealer Built", value: "DBT" },
    { displayName: "DealerTrack (Arkona)", value: "DTK" },
    { displayName: "Dominion Access (ACS)", value: "DOM" },
    { displayName: "PBS", value: "PBS" },
    { displayName: "Reynolds & Reynolds", value: "REY" },
    { displayName: "Tekion", value: "TEK" },
    { displayName: "DealerVault/Authenticom", value: "DVA" },
  ];

  // States with display name and code
  const states = [
    { displayName: "Alabama", code: "AL" },
    { displayName: "Alaska", code: "AK" },
    { displayName: "American Samoa", code: "AS" },
    { displayName: "Arizona", code: "AZ" },
    { displayName: "Arkansas", code: "AR" },
    { displayName: "California", code: "CA" },
    { displayName: "Colorado", code: "CO" },
    { displayName: "Connecticut", code: "CT" },
    { displayName: "Delaware", code: "DE" },
    { displayName: "District of Columbia", code: "DC" },
    { displayName: "Fed St Micronesia", code: "FM" },
    { displayName: "Florida", code: "FL" },
    { displayName: "Georgia", code: "GA" },
    { displayName: "Guam", code: "GU" },
    { displayName: "Hawaii", code: "HI" },
    { displayName: "Idaho", code: "ID" },
    { displayName: "Illinois", code: "IL" },
    { displayName: "Indiana", code: "IN" },
    { displayName: "Iowa", code: "IA" },
    { displayName: "Kansas", code: "KS" },
    { displayName: "Kentucky", code: "KY" },
    { displayName: "Louisiana", code: "LA" },
    { displayName: "Maine", code: "ME" },
    { displayName: "Marshall Islands", code: "MH" },
    { displayName: "Maryland", code: "MD" },
    { displayName: "Massachusetts", code: "MA" },
    { displayName: "Michigan", code: "MI" },
    { displayName: "Minnesota", code: "MN" },
    { displayName: "Mississippi", code: "MS" },
    { displayName: "Missouri", code: "MO" },
    { displayName: "Montana", code: "MT" },
    { displayName: "N Mariana Islands", code: "MP" },
    { displayName: "Nebraska", code: "NE" },
    { displayName: "Nevada", code: "NV" },
    { displayName: "New Hampshire", code: "NH" },
    { displayName: "New Jersey", code: "NJ" },
    { displayName: "New Mexico", code: "NM" },
    { displayName: "New York", code: "NY" },
    { displayName: "North Carolina", code: "NC" },
    { displayName: "North Dakota", code: "ND" },
    { displayName: "Ohio", code: "OH" },
    { displayName: "Oklahoma", code: "OK" },
    { displayName: "Oregon", code: "OR" },
    { displayName: "Palau", code: "PW" },
    { displayName: "Pennsylvania", code: "PA" },
    { displayName: "PuertoRico", code: "PR" },
    { displayName: "Rhode Island", code: "RI" },
    { displayName: "South Carolina", code: "SC" },
    { displayName: "South Dakota", code: "SD" },
    { displayName: "Tennessee", code: "TN" },
    { displayName: "Texas", code: "TX" },
    { displayName: "US Virgin Islands", code: "VI" },
    { displayName: "Utah", code: "UT" },
    { displayName: "Vermont", code: "VT" },
    { displayName: "Virginia", code: "VA" },
    { displayName: "Washington", code: "WA" },
    { displayName: "West Virginia", code: "WV" },
    { displayName: "Wisconsin", code: "WI" },
    { displayName: "Wyoming", code: "WY" },
    { displayName: "Alberta", code: "AB" },
    { displayName: "British Columbia", code: "BC" },
    { displayName: "Manitoba", code: "MB" },
    { displayName: "New Brunswick", code: "NB" },
    { displayName: "Newfoundland", code: "NF" },
    { displayName: "Nova Scotia", code: "NS" },
    { displayName: "Nunavut", code: "NU" },
    { displayName: "Northwest Territories", code: "NT" },
    { displayName: "Ontario", code: "ON" },
    { displayName: "Prince Edward Island", code: "PE" },
    { displayName: "Quebec", code: "PQ" },
    { displayName: "Saskatchewan", code: "SK" },
    { displayName: "Yukon Territory", code: "YT" },
  ];

  const profile = JSON.parse(localStorage.getItem("tokenExtract"));

  return (
    <div id="supportRequest">
      {/* Subpage Banner */}
      <div className="container-fluid subpage-banner text-center main-background">
        <h1>Support Request</h1>
        {profile?.org_name && (
          <div className="auto-h3">
            Submitted on Behalf of <span>{profile.org_name}</span>
          </div>
        )}
      </div>

      <div className="container">
        <div className="tab-content">
          <div className="manage-card active">
            <div>
              <div className="card-body accountContentArea mb-3 mt-3 bx-shadow">
                <div id="include" className="contentArea">
                  <div className="container">
                    <Formik
                      initialValues={initialValues}
                      validationSchema={getValidationSchema()}
                      validateOnChange={false}
                      validateOnBlur={false}
                      validate={(values) => {
                        // Don't check confirmation here - let required fields validate first
                        // Confirmation will be checked in handleSubmit after other validations pass
                        return {};
                      }}
                      onSubmit={handleSubmit}
                    >
                      {({ values, setFieldValue }) => {
                        const isDataCorrectionRequest =
                          values.supportType === "DCR";
                        const isServiceDataSetup = values.supportType === "SDS";
                        const currentCorrectionTip =
                          correctionTips[values.correctionType] || null;

                        return (
                          <Form>
                            {/* API Error Messages */}
                            {Object.keys(apiErrors).length > 0 && (
                              <div
                                className="alert alert-danger mt-3 mb-3"
                                role="alert"
                              >
                                {Object.entries(apiErrors).map(
                                  ([key, error], index) => (
                                    <div key={index}>
                                      {typeof error === "string"
                                        ? error
                                        : JSON.stringify(error)}
                                    </div>
                                  )
                                )}
                              </div>
                            )}

                            {/* Your Information Section */}
                            <div className="row mb-2">
                              <div className="col">
                                <div className="pb-2 auto-h4 header-blue">
                                  Your Information
                                </div>
                              </div>
                            </div>
                            <div className="row mt-3 mb-2">
                              <div className="col-12 col-md-6">
                                <Field
                                  type="text"
                                  id="firstName"
                                  name="firstName"
                                  className="form-control-lg w-100 mb-3 drop-down"
                                  maxLength={20}
                                  placeholder="First Name *"
                                />
                                <ErrorMessage
                                  name="firstName"
                                  component="div"
                                  className="text-danger small mt-1"
                                />
                              </div>
                              <div className="col-12 col-md-6">
                                <Field
                                  type="text"
                                  id="lastName"
                                  name="lastName"
                                  className="form-control-lg w-100 mb-3 drop-down"
                                  maxLength={35}
                                  placeholder="Last Name *"
                                />
                                <ErrorMessage
                                  name="lastName"
                                  component="div"
                                  className="text-danger small mt-1"
                                />
                              </div>
                            </div>
                            <div className="row mt-2 mb-3">
                              <div className="col-12 col-md-6">
                                <Field
                                  type="email"
                                  id="email"
                                  name="email"
                                  className="form-control-lg w-100 mb-3 drop-down"
                                  maxLength={50}
                                  placeholder="Email *"
                                  title="Enter a valid email address (e.g., example@domain.com)"
                                />
                                <ErrorMessage
                                  name="email"
                                  component="div"
                                  className="text-danger"
                                />
                              </div>
                              <div className="col-12 col-md-6">
                                <Field
                                  type="email"
                                  id="confirmEmail"
                                  name="confirmEmail"
                                  className="form-control-lg w-100 mb-3 drop-down"
                                  maxLength={50}
                                  placeholder="Confirm Email *"
                                  title="Enter a valid email address (e.g., example@domain.com)"
                                />
                                <ErrorMessage
                                  name="confirmEmail"
                                  component="div"
                                  className="text-danger"
                                />
                              </div>
                            </div>

                            {/* Dealer Information Section - matching legacy structure */}
                            <div className="row mb-2">
                              <div className="col">
                                <div className="pb-2 auto-h4 header-blue">
                                  Dealer Information
                                </div>
                              </div>
                            </div>
                            <div className="row mt-2 mb-3">
                              <div className="col-12 col-md-6">
                                <Field
                                  type="text"
                                  id="autocheckId"
                                  name="autocheckId"
                                  className="form-control-lg w-100 mb-3 drop-down"
                                  maxLength={7}
                                  placeholder="AutoCheck ID *"
                                />
                                <ErrorMessage
                                  name="autocheckId"
                                  component="div"
                                  className="text-danger"
                                />
                              </div>
                              <div className="col-12 col-md-6">
                                <Field
                                  type="text"
                                  id="coxId"
                                  name="coxId"
                                  className="form-control-lg w-100 mb-3 drop-down"
                                  maxLength={10}
                                  placeholder="Cox ID *"
                                />
                                <ErrorMessage
                                  name="coxId"
                                  component="div"
                                  className="text-danger"
                                />
                              </div>
                            </div>

                            {/* Support Type Section */}
                            <div className="row mt-5 background-box">
                              <div className="col">
                                <div className="pt-3 pb-3 auto-h4 header-blue">
                                  What type of support do you need?
                                </div>
                              </div>
                            </div>
                            <div className="row background-box">
                              <div className="form-group col-12 col-md-6 select-arrow">
                                <Field
                                  as="select"
                                  id="supportType"
                                  name="supportType"
                                  className={`form-control-lg w-100 drop-down${
                                    values.supportType ? "" : " placeholder"
                                  }`}
                                  onChange={(e) => {
                                    const newSupportType = e.target.value;
                                    setFieldValue(
                                      "supportType",
                                      newSupportType
                                    );
                                    // Clear conditional fields when support type changes
                                    if (newSupportType !== "DCR") {
                                      setFieldValue("vin", "");
                                      setFieldValue("disputedDate", "");
                                      setFieldValue("correctionType", "");
                                    }
                                    if (newSupportType !== "SDS") {
                                      setFieldValue("dmsType", "");
                                      setFieldValue("dealershipName", "");
                                      setFieldValue("dealershipAddress", "");
                                      setFieldValue("dealershipCity", "");
                                      setFieldValue("dealershipState", "");
                                      setFieldValue("dealershipZip", "");
                                      setFieldValue("dealershipPhone", "");
                                      setFieldValue("dmsDealerNumber", "");
                                      setFieldValue("techContactName", "");
                                      setFieldValue("techContactPhone", "");
                                      setFieldValue("techContactEmail", "");
                                      setFieldValue(
                                        "techContactConfirmEmail",
                                        ""
                                      );
                                    }
                                  }}
                                >
                                  <option value="">Select One *</option>
                                  <option value="DCR">
                                    Data Correction Request
                                  </option>
                                  <option value="AUPR">
                                    Account Unlock or Password Reset
                                  </option>
                                  <option value="SDS">
                                    Service Data Setup
                                  </option>
                                  <option value="CPU">
                                    Contract Plan Update
                                  </option>
                                  <option value="OT">Other</option>
                                </Field>
                                <ErrorMessage
                                  name="supportType"
                                  component="div"
                                  className="text-danger small mt-1"
                                />
                              </div>
                            </div>

                            {/* Service Data Setup Section */}
                            {isServiceDataSetup && (
                              <>
                                {/* DMS Type Section - matching structure with Your Information */}
                                <div className="row mb-2">
                                  <div className="col">
                                    <div className="pb-2 auto-h4 header-blue">
                                      DMS Type
                                    </div>
                                  </div>
                                </div>
                                <div className="row mt-3 mb-2">
                                  <div className="form-group col-12 col-md-6 select-arrow">
                                    <Field
                                      as="select"
                                      id="dmsType"
                                      name="dmsType"
                                      className={`form-control-lg w-100 drop-down${
                                        values.dmsType ? "" : " placeholder"
                                      }`}
                                    >
                                      <option value="">Select One *</option>
                                      {dmsTypes.map((type) => (
                                        <option
                                          key={type.value}
                                          value={type.value}
                                        >
                                          {type.displayName}
                                        </option>
                                      ))}
                                    </Field>
                                    <ErrorMessage
                                      name="dmsType"
                                      component="div"
                                      className="text-danger"
                                    />
                                  </div>
                                </div>

                                {/* Dealership Information */}
                                <div className="row mb-2 mt-4">
                                  <div className="col">
                                    <div className="pb-2 auto-h4 header-blue">
                                      Dealership information for DMS connection
                                    </div>
                                  </div>
                                </div>
                                <div className="row mt-3 mb-2">
                                  <div className="col-12 col-md-6">
                                    <Field
                                      type="text"
                                      id="dealershipName"
                                      name="dealershipName"
                                      className="form-control-lg w-100 mb-3 drop-down"
                                      maxLength={50}
                                      placeholder="Dealership Name *"
                                      pattern="[A-Za-z0-9 .'\-&]+"
                                    />
                                    <ErrorMessage
                                      name="dealershipName"
                                      component="div"
                                      className="text-danger"
                                    />
                                  </div>
                                </div>
                                <div className="row mt-2 mb-3">
                                  <div className="col-12 col-md-6">
                                    <Field
                                      type="text"
                                      id="dealershipAddress"
                                      name="dealershipAddress"
                                      className="form-control-lg w-100 mb-3 drop-down"
                                      maxLength={150}
                                      placeholder="Dealership Address *"
                                      pattern="[A-Za-z0-9 .,'#\-]+"
                                    />
                                    <ErrorMessage
                                      name="dealershipAddress"
                                      component="div"
                                      className="text-danger"
                                    />
                                  </div>
                                  <div className="col-12 col-md-6">
                                    <Field
                                      type="text"
                                      id="dealershipCity"
                                      name="dealershipCity"
                                      className="form-control-lg w-100 mb-3 drop-down"
                                      maxLength={100}
                                      placeholder="Dealership City *"
                                      pattern="[A-Za-z .'\-]+"
                                    />
                                    <ErrorMessage
                                      name="dealershipCity"
                                      component="div"
                                      className="text-danger"
                                    />
                                  </div>
                                </div>
                                <div className="row mt-2 mb-3">
                                  <div className="form-group col-12 col-md-6 select-arrow">
                                    <Field
                                      as="select"
                                      id="dealershipState"
                                      name="dealershipState"
                                      className={`form-control-lg w-100 drop-down${
                                        values.dealershipState
                                          ? ""
                                          : " placeholder"
                                      }`}
                                    >
                                      <option value="">
                                        Dealership State *
                                      </option>
                                      {states.map((state) => (
                                        <option
                                          key={state.code}
                                          value={state.code}
                                        >
                                          {state.displayName}
                                        </option>
                                      ))}
                                    </Field>
                                    <ErrorMessage
                                      name="dealershipState"
                                      component="div"
                                      className="text-danger"
                                    />
                                  </div>
                                  <div className="col-12 col-md-6">
                                    <Field
                                      type="text"
                                      id="dealershipZip"
                                      name="dealershipZip"
                                      className="form-control-lg w-100 mb-3 drop-down"
                                      maxLength={10}
                                      placeholder="Dealership ZipCode *"
                                      pattern="\d{5}(-\d{4})?|[A-Za-z]\d[A-Za-z] ?\d[A-Za-z]\d"
                                    />
                                    <ErrorMessage
                                      name="dealershipZip"
                                      component="div"
                                      className="text-danger"
                                    />
                                  </div>
                                </div>
                                <div className="row mt-2 mb-3">
                                  <div className="col-12 col-md-6">
                                    <Field
                                      type="text"
                                      id="dealershipPhone"
                                      name="dealershipPhone"
                                      className="form-control-lg w-100 mb-3 drop-down"
                                      maxLength={14}
                                      placeholder="Dealership Phone *"
                                      pattern="^(\d{3}-\d{3}-\d{4}|\(\d{3}\)\d{3}-\d{4})$"
                                      title="Enter a valid phone number 123-456-7890 or (123)456-7890."
                                    />
                                    <ErrorMessage
                                      name="dealershipPhone"
                                      component="div"
                                      className="text-danger"
                                    />
                                  </div>
                                  <div className="col-12 col-md-6">
                                    <Field
                                      type="text"
                                      id="dmsDealerNumber"
                                      name="dmsDealerNumber"
                                      className="form-control-lg w-100 mb-3 drop-down"
                                      maxLength={35}
                                      placeholder="DMS-Specific Dealer Number *"
                                      pattern="[A-Za-z0-9]+"
                                    />
                                    <ErrorMessage
                                      name="dmsDealerNumber"
                                      component="div"
                                      className="text-danger"
                                    />
                                  </div>
                                </div>

                                {/* Technical Contact Information */}
                                <div className="mt-4 pt-4 border-top">
                                  <h4 className="auto-h4 header-blue mb-3">
                                    Dealership technical contact information
                                  </h4>
                                  <p className="mb-3">
                                    This contact must have familiarity and
                                    access to the DMS selected.
                                  </p>
                                  <div className="row">
                                    <div className="col-md-6 mb-3">
                                      <label
                                        htmlFor="techContactName"
                                        className="form-label"
                                      >
                                        Tech Contact Name{" "}
                                        <span className="text-danger">*</span>
                                      </label>
                                      <Field
                                        type="text"
                                        id="techContactName"
                                        name="techContactName"
                                        className="form-control form-control-lg drop-down"
                                        maxLength={50}
                                        placeholder="Tech Contact Name *"
                                        pattern="[A-Za-z .'\-]+"
                                      />
                                      <ErrorMessage
                                        name="techContactName"
                                        component="div"
                                        className="text-danger small mt-1"
                                      />
                                    </div>
                                    <div className="col-md-6 mb-3">
                                      <label
                                        htmlFor="techContactPhone"
                                        className="form-label"
                                      >
                                        Tech Contact Phone{" "}
                                        <span className="text-danger">*</span>
                                      </label>
                                      <Field
                                        type="text"
                                        id="techContactPhone"
                                        name="techContactPhone"
                                        className="form-control form-control-lg drop-down"
                                        maxLength={14}
                                        placeholder="Tech Contact Phone *"
                                        pattern="^(\d{3}-\d{3}-\d{4}|\(\d{3}\)\d{3}-\d{4})$"
                                        title="Enter a valid phone number 123-456-7890 or (123)456-7890."
                                      />
                                      <ErrorMessage
                                        name="techContactPhone"
                                        component="div"
                                        className="text-danger small mt-1"
                                      />
                                    </div>
                                  </div>
                                  <div className="row">
                                    <div className="col-md-6 mb-3">
                                      <label
                                        htmlFor="techContactEmail"
                                        className="form-label"
                                      >
                                        Tech Contact Email{" "}
                                        <span className="text-danger">*</span>
                                      </label>
                                      <Field
                                        type="email"
                                        id="techContactEmail"
                                        name="techContactEmail"
                                        className="form-control form-control-lg drop-down"
                                        maxLength={50}
                                        placeholder="Tech Contact Email *"
                                        pattern="[a-zA-Z0-9._%+\-]+@[a-zA-Z0-9.\-]+\.[a-zA-Z]{2,}"
                                        title="Enter a valid email address (e.g., example@domain.com)"
                                      />
                                      <ErrorMessage
                                        name="techContactEmail"
                                        component="div"
                                        className="text-danger small mt-1"
                                      />
                                    </div>
                                    <div className="col-md-6 mb-3">
                                      <label
                                        htmlFor="techContactConfirmEmail"
                                        className="form-label"
                                      >
                                        Confirm Tech Contact Email{" "}
                                        <span className="text-danger">*</span>
                                      </label>
                                      <Field
                                        type="email"
                                        id="techContactConfirmEmail"
                                        name="techContactConfirmEmail"
                                        className="form-control form-control-lg drop-down"
                                        maxLength={50}
                                        placeholder="Confirm Tech Contact Email *"
                                        pattern="[a-zA-Z0-9._%+\-]+@[a-zA-Z0-9.\-]+\.[a-zA-Z]{2,}"
                                        title="Enter a valid email address (e.g., example@domain.com)"
                                      />
                                      <ErrorMessage
                                        name="techContactConfirmEmail"
                                        component="div"
                                        className="text-danger small mt-1"
                                      />
                                    </div>
                                  </div>
                                </div>

                                {/* DMS Authorization Forms */}
                                <div className="mt-4 pt-4 border-top">
                                  <h4 className="auto-h4 header-blue mb-3">
                                    DMS Authorization Forms
                                  </h4>
                                  <p className="mb-3">
                                    One form per dealership/DMS dealer number is
                                    required for the following DMS providers -
                                    AutoSoft, Automate, Dealer Built, Dominion
                                    Access (ACS), PBS, and Tekion.
                                  </p>
                                  {[1, 2, 3, 4].map((num) => (
                                    <div
                                      key={num}
                                      className="custom-file upload-support-file"
                                    >
                                      <input
                                        type="file"
                                        className="custom-file-input form-control"
                                        name={`dmsFile${num}`}
                                        id={`dmsFile${num}`}
                                        accept="application/pdf,image/jpeg,image/png"
                                        onChange={(e) => {
                                          handleFileChange(
                                            `dmsFile${num}`,
                                            e.target.files[0],
                                            setFieldValue
                                          );
                                        }}
                                      />
                                      <label
                                        className="custom-file-label form-control"
                                        htmlFor={`dmsFile${num}`}
                                      >
                                        {fileNames[`dmsFile${num}`] ||
                                          "Choose file"}
                                      </label>
                                    </div>
                                  ))}
                                  <div className="text-muted small mt-2">
                                    Attach Documents (Limit of 4 Attachments,
                                    Maximum total size: 7 MB, PDF/JPG/PNG
                                    Format)
                                  </div>
                                  <div className="mt-3">
                                    <h5 className="auto-h4 header-blue mb-2">
                                      Supporting Documents Detail
                                    </h5>
                                    <p className="small">
                                      Please attach all supporting documents
                                      before submitting this request. If you are
                                      unable to attach documentation now, send
                                      to Experian by Email:{" "}
                                      <a href="mailto:AutoCheck_ServiceData@experian.com">
                                        AutoCheck_ServiceData@experian.com
                                      </a>
                                    </p>
                                  </div>
                                </div>
                              </>
                            )}

                            {/* VIN Entry Section (for DCR) - matching legacy structure */}
                            {isDataCorrectionRequest && (
                              <div id="vinSection">
                                <div className="row mt-4 background-box">
                                  <div className="col">
                                    <div className="mt-3 pb-4 auto-h4 header-blue">
                                      Enter VIN
                                    </div>
                                  </div>
                                </div>
                                <div className="row mb-3 background-box">
                                  <div className="col-12 col-md-5 mb-1">
                                    <Field
                                      type="text"
                                      id="vin"
                                      name="vin"
                                      className="form-control-lg w-100 mb-3"
                                      maxLength={17}
                                      placeholder="Enter VIN *"
                                    />
                                    <ErrorMessage
                                      name="vin"
                                      component="div"
                                      className="text-danger"
                                    />
                                  </div>
                                </div>
                              </div>
                            )}

                            {/* Correction Section (for DCR) - matching legacy structure */}
                            {isDataCorrectionRequest && (
                              <div id="correctionSection">
                                <div className="row mt-4 background-box">
                                  <div className="col">
                                    <div className="pt-3 pb-3 auto-h4 header-blue">
                                      What would you like to correct?
                                    </div>
                                  </div>
                                </div>
                                <div className="row background-box">
                                  <div className="form-group col-12 col-md-5 select-arrow">
                                    <Field
                                      as="select"
                                      id="correctionType"
                                      name="correctionType"
                                      className={`form-control-lg w-100 drop-down${
                                        values.correctionType
                                          ? ""
                                          : " placeholder"
                                      }`}
                                    >
                                      <option value="">Select One *</option>
                                      <option value="AC">Accident</option>
                                      <option value="AA">
                                        Auction Announcement
                                      </option>
                                      <option value="ST">
                                        State Title Brand
                                      </option>
                                      <option value="DS">
                                        Damage Severity
                                      </option>
                                      <option value="OR">
                                        Odometer Reading
                                      </option>
                                      <option value="OT">Other</option>
                                    </Field>
                                    <ErrorMessage
                                      name="correctionType"
                                      component="div"
                                      className="text-danger"
                                    />
                                  </div>
                                </div>

                                {/* Correction Tips and File Uploads - only show when correction type is selected - shown BEFORE Additional Information */}
                                {values.correctionType && (
                                  <div
                                    id="correctionTip"
                                    className="row pt-2 pb-2 background-box"
                                  >
                                    <div className="col">
                                      {currentCorrectionTip && (
                                        <div
                                          id="tip-box"
                                          className="mt-3 mb-4 pl-3 pr-2 pt-3 tip-box"
                                        >
                                          <div className="auto-h4 header-blue mb-2">
                                            What you will need to provide:
                                          </div>
                                          <div>{currentCorrectionTip}</div>
                                        </div>
                                      )}
                                      <div className="pb-3">
                                        {[1, 2, 3, 4].map((num) => (
                                          <div
                                            key={num}
                                            className="custom-file upload-support-file"
                                          >
                                            <input
                                              type="file"
                                              className="custom-file-input form-control"
                                              name={`supportFile${num}`}
                                              id={`supportFile${num}`}
                                              accept="application/pdf,image/jpeg,image/png"
                                              onChange={(e) => {
                                                handleFileChange(
                                                  `supportFile${num}`,
                                                  e.target.files[0],
                                                  setFieldValue
                                                );
                                              }}
                                            />
                                            <label
                                              className="custom-file-label form-control"
                                              htmlFor={`supportFile${num}`}
                                            >
                                              {fileNames[`supportFile${num}`] ||
                                                "Choose file"}
                                            </label>
                                          </div>
                                        ))}
                                        <div className="pt-3">
                                          Attach Documents (Limit of 4
                                          Attachments, Maximum total size: 7 MB,
                                          PDF/JPG/PNG Format)
                                        </div>
                                      </div>
                                      <div className="pt-2 pb-2 auto-h4 header-blue">
                                        Supporting Documents Detail
                                      </div>
                                      <div>
                                        Please attach all supporting documents
                                        before submitting this request. If you
                                        are unable to attach documentation now,
                                        send to Experian by Email:{" "}
                                        <a
                                          href="mailto:AutoCheckSupport@experian.com"
                                          aria-label="Contact us via email"
                                        >
                                          AutoCheckSupport@experian.com
                                        </a>
                                      </div>
                                    </div>
                                  </div>
                                )}

                                {/* Additional Information - Disputed Date - shown AFTER file uploads, matching legacy structure */}
                                <div className="row mt-5 mb-1">
                                  <div className="col-12 col-md-8">
                                    <div className="pb-4 auto-h4 header-blue">
                                      Additional Information
                                    </div>
                                    <div
                                      id="datepicker-container"
                                      className="mb-3"
                                    >
                                      <div
                                        className="datepicker date input-group dateParent"
                                        style={{ maxWidth: "400px" }}
                                      >
                                        <input
                                          type="date"
                                          id="disputedDateNative"
                                          className="form-control-lg date-field-radius dateChild date-font native-date-input"
                                          style={{
                                            opacity: 0,
                                            position: "absolute",
                                            pointerEvents: "none",
                                            zIndex: -1,
                                          }}
                                          onChange={(e) => {
                                            const dateValue = e.target.value;
                                            if (dateValue) {
                                              // Convert YYYY-MM-DD to MM/DD/YYYY
                                              const [year, month, day] =
                                                dateValue.split("-");
                                              const formattedDate = `${month}/${day}/${year}`;
                                              setFieldValue(
                                                "disputedDate",
                                                formattedDate
                                              );
                                              e.target.value = dateValue;
                                            }
                                          }}
                                          value={
                                            values.disputedDate &&
                                            values.disputedDate.match(
                                              /^\d{2}\/\d{2}\/\d{4}$/
                                            )
                                              ? (() => {
                                                  const [month, day, year] =
                                                    values.disputedDate.split(
                                                      "/"
                                                    );
                                                  return `${year}-${month}-${day}`;
                                                })()
                                              : ""
                                          }
                                        />
                                        <Field
                                          type="text"
                                          id="disputedDate"
                                          name="disputedDate"
                                          className="form-control-lg date-field-radius dateChild date-font"
                                          placeholder="Disputed Record Date *"
                                          maxLength={10}
                                          value={values.disputedDate || ""}
                                          onChange={(e) => {
                                            setFieldValue(
                                              "disputedDate",
                                              e.target.value
                                            );
                                          }}
                                          onClick={() => {
                                            const nativeInput =
                                              document.getElementById(
                                                "disputedDateNative"
                                              );
                                            if (
                                              nativeInput &&
                                              typeof nativeInput.showPicker ===
                                                "function"
                                            ) {
                                              nativeInput.showPicker();
                                            }
                                          }}
                                          style={{
                                            width: "auto",
                                            flex: "1 1 auto",
                                          }}
                                        />
                                        <span className="input-group-append">
                                          <span className="input-group-text calendar-icon-box dateChild">
                                            <i
                                              className="fa fa-calendar"
                                              onClick={() => {
                                                const nativeInput =
                                                  document.getElementById(
                                                    "disputedDateNative"
                                                  );
                                                if (
                                                  nativeInput &&
                                                  typeof nativeInput.showPicker ===
                                                    "function"
                                                ) {
                                                  nativeInput.showPicker();
                                                }
                                              }}
                                              style={{ cursor: "pointer" }}
                                            ></i>
                                          </span>
                                        </span>
                                      </div>
                                    </div>
                                    <p className="mt-2">
                                      Please enter the Event Date from Detailed
                                      Vehicle History that corresponds with the
                                      record you would like to dispute.
                                    </p>
                                    <ErrorMessage
                                      name="disputedDate"
                                      component="div"
                                      className="text-danger"
                                    />
                                  </div>
                                </div>
                              </div>
                            )}

                            {/* Request Detail - Always shown - matching legacy structure exactly */}
                            <div className="row mt-2 mb-3">
                              <div className="col">
                                <Field
                                  as="textarea"
                                  id="details"
                                  name="details"
                                  className="form-control-lg w-100"
                                  rows="9"
                                  cols="60"
                                  maxLength={600}
                                  placeholder="Request Detail"
                                  onChange={(e) => {
                                    const value = e.target.value.replace(
                                      /[\n\r]+/g,
                                      " "
                                    );
                                    setFieldValue("details", value);
                                  }}
                                />
                                <div className="mt-2 mb-3">
                                  <span>
                                    You have{" "}
                                    <input
                                      readOnly
                                      type="text"
                                      name="countdown"
                                      id="countdown"
                                      size="3"
                                      value={
                                        600 - (values.details?.length || 0)
                                      }
                                      className="text-center chars-left"
                                    />{" "}
                                    characters left.
                                  </span>
                                </div>
                                <div className="mb-3">
                                  <span className="error-text-red">
                                    * Required Field
                                  </span>
                                </div>
                              </div>
                            </div>

                            {/* Terms and Conditions Section - matching legacy structure */}
                            <div className="row mt-5 mb-3">
                              <div className="col">
                                <div className="pb-4 auto-h4 header-blue">
                                  Terms and Conditions
                                </div>
                                <div>
                                  By submitting this form, you confirm and
                                  attest that, to the best of your knowledge,
                                  all data included in this submission is true
                                  and accurate as of the time of submission.
                                  Documents and information submitted through
                                  this request will be used for research into
                                  accuracy of reported data and determination of
                                  the need for requested correction. Completed
                                  data corrections are recorded and may be
                                  referenced in future data disputes. Experian
                                  reserves the right to deny data correction
                                  requests in the instance that insufficient
                                  information can be provided to necessitate a
                                  correction. Experian reserves the right to
                                  reverse completed data corrections in the
                                  instance that a future data dispute deems
                                  previous corrections to be incorrect.
                                </div>
                              </div>
                            </div>

                            {/* Confirmation - matching legacy structure */}
                            <div className="row mt-5 mb-3">
                              <div className="col">
                                <div className="pb-3 auto-h4 header-blue">
                                  Confirmation
                                </div>
                                <div>
                                  <Field
                                    type="checkbox"
                                    id="confirmation"
                                    name="confirmation"
                                  />{" "}
                                  Please select this checkbox if you agree to
                                  the Terms and Conditions Stated above.
                                  <ErrorMessage
                                    name="confirmation"
                                    component="div"
                                    className="text-danger"
                                  />
                                </div>
                              </div>
                            </div>

                            {/* Submit Button - matching legacy structure */}
                            <div className="row mb-4">
                              <div className="col">
                                <div className="mb-3">
                                  By clicking "Submit" I acknowledge that I have
                                  read and agree to the Terms and Conditions
                                  above.
                                </div>
                                <button
                                  type="submit"
                                  className="btn btn-block btn-experian-purple col-12 col-md-4"
                                  aria-label="Submit"
                                >
                                  Submit
                                </button>
                              </div>
                            </div>
                          </Form>
                        );
                      }}
                    </Formik>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SupportRequest;
